import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, FolderOpen, FileText, Download, ClipboardList, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

function DocColumn({ title, subtitle, icon: Icon, docs, testid }) {
  return (
    <div data-testid={testid} className="bg-white rounded-2xl border border-[#E5DFD7] shadow-sm p-6 flex flex-col">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-xl bg-[#4A2E2B] flex items-center justify-center"><Icon className="h-5 w-5 text-[#E5A93C]" /></div>
        <div>
          <h3 className="font-display text-base md:text-lg font-semibold text-[#2A1F1B] leading-tight">{title}</h3>
          <p className="text-xs text-[#8C827A]">{subtitle}</p>
        </div>
      </div>
      {docs.length === 0 ? (
        <p className="text-sm text-[#8C827A] py-6 text-center border border-dashed border-[#E5DFD7] rounded-xl">Belum ada berkas yang dipublikasikan.</p>
      ) : (
        <ul className="divide-y divide-[#F1ECE5]">
          {docs.map((d) => (
            <li key={d.id}>
              <a href={`${api.defaults.baseURL}/documents/${d.id}/download`} data-testid="public-doc-download"
                className="group flex items-center gap-3 py-3 hover:bg-[#F7F4EF] -mx-2 px-2 rounded-lg transition-colors">
                <FileText className="h-5 w-5 text-[#B85C38] shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-[#1F1917] truncate">{d.title}</div>
                  {d.description && <div className="text-xs text-[#8C827A] truncate">{d.description}</div>}
                </div>
                <Download className="h-4 w-4 text-[#8C827A] group-hover:text-[#B85C38] transition-colors" />
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function ResourcesSection() {
  const [docs, setDocs] = useState([]);
  useEffect(() => {
    api.get("/documents").then(({ data }) => setDocs(data)).catch(() => {});
  }, []);

  return (
    <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20" data-testid="landing-resources">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold text-[#B85C38] mb-2">Pusat Unduhan</div>
          <h2 className="font-display text-base md:text-lg font-semibold text-[#1F1917]">Panduan, Materi & Aturan Kepramukaan</h2>
        </div>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-2xl bg-[#4A2E2B] text-white p-6 flex flex-col justify-between">
          <div className="absolute -top-10 -right-10 h-40 w-40 rounded-full bg-[#E5A93C]/20" />
          <div className="relative">
            <div className="h-11 w-11 rounded-xl bg-[#E5A93C] flex items-center justify-center mb-4"><ClipboardList className="h-5 w-5 text-[#2A1F1B]" /></div>
            <h3 className="font-display text-base md:text-lg font-semibold">Petunjuk Pengisian Data</h3>
            <p className="text-sm text-white/75 mt-2 leading-relaxed">Langkah demi langkah membuat akun, melengkapi profil, mengajukan prestasi, hingga terverifikasi Kwarcab.</p>
          </div>
          <Link to="/petunjuk" data-testid="landing-guide-btn"
            className="relative mt-6 inline-flex items-center justify-center gap-2 bg-[#E5A93C] text-[#2A1F1B] px-5 py-3 rounded-full font-bold hover:bg-[#d69a2b] transition-colors">
            Buka Petunjuk <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
        <DocColumn title="Unduh Materi" subtitle="Bahan ajar & materi kepramukaan" icon={BookOpen} testid="landing-materi-column"
          docs={docs.filter((d) => d.category === "materi")} />
        <DocColumn title="Unduh Aturan Kepramukaan" subtitle="AD/ART, PP, Jukran & regulasi" icon={FolderOpen} testid="landing-aturan-column"
          docs={docs.filter((d) => d.category === "aturan")} />
      </div>
    </section>
  );
}
